import { useEffect, useRef, useState } from 'react';
import type { PublicLobbyState, Preset, Role } from '@schemer/shared';
import { api, ApiError } from '../lib/api.js';
import { tokenStore } from '../lib/storage.js';
import { navigate } from '../lib/route.js';
import { getSocket } from '../lib/socket.js';

// 12 — the mode a newcomer is stepping into.
const PRESET_LABEL: Record<Preset, string> = {
  introductie: 'Introductie',
  familie: 'Familie',
  klassiek: 'Klassiek',
  uitdaging: 'Uitdaging',
  eigen: 'Eigen samenstelling',
};

// Dutch labels for the test-mode role pin.
const ROLE_LABELS: Record<Role, string> = {
  vuurwachter: 'Vuurwachter',
  ziener: 'Ziener',
  beschermer: 'Beschermer',
  shapeshifter: 'Shapeshifter',
  schaduw: 'Schaduw',
};

type State =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      lobby: PublicLobbyState;
      selfPlayerId: string | null;
      shareUrl: string;
    }
  | { kind: 'error'; message: string };

export function LobbyScreen({ code }: { code: string }): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [starting, setStarting] = useState(false);
  const [addingBot, setAddingBot] = useState(false);
  // Test tool: the host may pin their own role ('' = random). Fast mode only.
  const [forcedRole, setForcedRole] = useState<Role | ''>('');
  const [copied, setCopied] = useState(false);
  const socketRef = useRef<ReturnType<typeof getSocket> | null>(null);

  // Initial REST fetch, then subscribe to live updates over Socket.IO.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getLobby(code);
        if (cancelled) return;
        setState({
          kind: 'ready',
          lobby: res.lobby,
          selfPlayerId: res.selfPlayerId,
          shareUrl: res.shareUrl,
        });

        // If status already left lobby (server restart, late join), bounce.
        if (res.lobby.status !== 'lobby') {
          // M2 will route to the actual game screen; placeholder for now.
          return;
        }

        const deviceToken = tokenStore.get();
        if (!deviceToken) return; // can still view, just won't get live updates

        const sock = getSocket();
        socketRef.current = sock;
        sock.emit('lobby:subscribe', { code, deviceToken }, (ack) => {
          if (cancelled) return;
          if (!ack.ok) {
            setState({ kind: 'error', message: ack.error });
            return;
          }
          // Server confirms with the current state; use it as the baseline.
          setState((prev) => (prev.kind === 'ready' ? { ...prev, lobby: ack.state } : prev));
        });
        sock.on('lobby:state', (next) => {
          if (cancelled) return;
          setState((prev) => (prev.kind === 'ready' ? { ...prev, lobby: next } : prev));
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof ApiError ? err.message || err.code : 'kon de lobby niet laden',
        });
      }
    })();
    return () => {
      cancelled = true;
      const sock = socketRef.current;
      if (sock) {
        sock.emit('lobby:unsubscribe');
        sock.off('lobby:state');
      }
    };
  }, [code]);

  async function onStart() {
    if (state.kind !== 'ready') return;
    setStarting(true);
    try {
      const res = await api.startGame(code, forcedRole ? { forcedRole } : undefined);
      setState({ ...state, lobby: res.lobby });
    } catch (err) {
      console.error('start failed', err);
      if (err instanceof ApiError) {
        alert(`starten mislukt (${err.status} ${err.code}): ${err.message}`);
      } else if (err instanceof Error) {
        alert(`starten mislukt: ${err.message}`);
      } else {
        alert('starten mislukt: onbekende fout');
      }
    } finally {
      setStarting(false);
    }
  }

  async function onAddBot() {
    if (state.kind !== 'ready') return;
    setAddingBot(true);
    try {
      const res = await api.addBot(code);
      setState({ ...state, lobby: res.lobby });
    } catch (err) {
      console.error('add bot failed', err);
      alert(
        err instanceof ApiError ? `bot toevoegen mislukt: ${err.message}` : 'bot toevoegen mislukt',
      );
    } finally {
      setAddingBot(false);
    }
  }

  async function copyShareUrl() {
    if (state.kind !== 'ready') return;
    try {
      await navigator.clipboard.writeText(state.shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — fall through; user can long-press to copy
    }
  }

  // Native share sheet (WhatsApp, Messages, …) on phones that support it.
  async function shareInvite() {
    if (state.kind !== 'ready') return;
    try {
      await navigator.share({
        title: 'Schemer',
        text: `Doe je mee met mijn Schemer-spel in Willowbrook? Code ${state.lobby.code} — tik de link om erbij te komen.`,
        url: state.shareUrl,
      });
    } catch {
      // user dismissed the sheet, or share failed — no-op (copy stays available)
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="lobby">
        <p>lobby laden…</p>
      </main>
    );
  }
  if (state.kind === 'error') {
    return (
      <main className="lobby">
        <button className="back" onClick={() => navigate({ name: 'splash' })}>
          ← terug
        </button>
        <p className="bad">{state.message}</p>
      </main>
    );
  }

  const { lobby, selfPlayerId, shareUrl } = state;
  const self = lobby.players.find((p) => p.id === selfPlayerId) ?? null;
  const isHost = !!self?.isHost;
  const enoughPlayers = lobby.players.length >= lobby.minPlayers;
  // Bots are a test tool — only offered on fast-clock games, never a real potje.
  const isFast = lobby.schedule.clockMode === 'fast';
  const lobbyFull = lobby.players.length >= 16;
  // Web Share API is a phone thing; on desktop we just show the copy button.
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  if (lobby.status === 'running' || lobby.status === 'finished') {
    navigate({ name: 'game', code: lobby.code });
    return (
      <main className="lobby">
        <p className="subtitle">Willowbrook binnengaan…</p>
      </main>
    );
  }

  return (
    <main className="lobby">
      <header>
        <p className="subtitle">Willowbrook</p>
        <h1 className="code">{lobby.code}</h1>
        <div className="share-actions">
          {canShare && (
            <button className="cta primary" onClick={shareInvite}>
              delen
            </button>
          )}
          <button className="cta" onClick={copyShareUrl}>
            {copied ? 'gekopieerd!' : canShare ? 'kopieer link' : 'kopieer uitnodigingslink'}
          </button>
        </div>
        {/* 10 — the raw URL is techy and long; it stays available for a manual
            copy but no longer competes with the two share actions. */}
        <p className="share-url" title={shareUrl}>
          {shareUrl}
        </p>
        {/* 12 — someone stepping in should see what they're stepping into. */}
        <p className="lobby-mode">
          {PRESET_LABEL[lobby.preset] ?? lobby.preset} · {lobby.minPlayers}–20 spelers
        </p>
      </header>

      <section className="players">
        <h2>
          Spelers ({lobby.players.length}/{lobby.minPlayers})
        </h2>
        <ul>
          {lobby.players.map((p) => (
            <li key={p.id} className={p.id === selfPlayerId ? 'me' : ''}>
              <span className="name">{p.displayName}</span>
              {p.isHost && <span className="tag">host</span>}
              {p.isBot && <span className="tag">bot</span>}
              {p.id === selfPlayerId && <span className="tag self">jij</span>}
              {isHost && p.id !== selfPlayerId && (
                <KickButton code={code} targetId={p.id} targetName={p.displayName} />
              )}
              {isHost && p.isBot && (
                <BotAggression
                  code={code}
                  botId={p.id}
                  value={p.botAggression ?? 3}
                  onLobby={(l) => setState((s) => (s.kind === 'ready' ? { ...s, lobby: l } : s))}
                />
              )}
            </li>
          ))}
        </ul>
        {isHost && isFast && (
          <button className="cta" onClick={onAddBot} disabled={addingBot || lobbyFull}>
            {addingBot ? 'bot toevoegen…' : lobbyFull ? 'lobby vol' : '+ testbot toevoegen'}
          </button>
        )}
      </section>

      {isHost && isFast && lobby.assignableRoles.length > 0 && (
        <label className="role-pin">
          <span>jouw rol (test)</span>
          <select
            value={forcedRole}
            onChange={(e) => setForcedRole(e.target.value as Role | '')}
            disabled={starting}
          >
            <option value="">willekeurig</option>
            {lobby.assignableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
      )}

      <p className="lobby-hint">
        {isHost
          ? 'Deel de code. Zodra iedereen binnen is, start jij het spel.'
          : 'Je zit erin. Zodra iedereen binnen is, start de host het spel.'}
      </p>

      {isHost ? (
        <button className="cta primary" onClick={onStart} disabled={!enoughPlayers || starting}>
          {starting
            ? 'starten…'
            : enoughPlayers
              ? 'start het spel'
              : `nog ${lobby.minPlayers - lobby.players.length} nodig`}
        </button>
      ) : (
        <p className="subtitle">wachten tot de host start…</p>
      )}

      <LeaveButton code={code} where="lobby" />
    </main>
  );
}

function KickButton({
  code,
  targetId,
  targetName,
}: {
  code: string;
  targetId: string;
  targetName: string;
}) {
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!confirm(`${targetName} eruit sturen?`)) return;
    setBusy(true);
    try {
      await api.kick(code, { targetPlayerId: targetId });
    } catch (err) {
      console.error('kick failed', err);
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'eruit sturen mislukt');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="cta small" onClick={() => void go()} disabled={busy}>
      {busy ? '…' : 'eruit'}
    </button>
  );
}

const AGGR_LABEL = ['', 'rustig', 'kalm', 'normaal', 'fel', 'heftig'];

// Host-only per-bot chat aggression (1 rustig … 5 heftig), the "hoe fel praat
// deze bot" knob. Commits on release; the endpoint broadcasts the new lobby.
function BotAggression({
  code,
  botId,
  value,
  onLobby,
}: {
  code: string;
  botId: string;
  value: number;
  onLobby: (lobby: PublicLobbyState) => void;
}) {
  const [v, setV] = useState(value);
  const [busy, setBusy] = useState(false);
  async function commit(next: number) {
    setBusy(true);
    try {
      const r = await api.setBotAggression(code, { playerId: botId, aggression: next });
      onLobby(r.lobby);
    } catch (err) {
      console.error('aggression failed', err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <label className="aggr">
      <span className="aggr-lbl">felheid</span>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={v}
        disabled={busy}
        onChange={(e) => setV(Number(e.target.value))}
        onMouseUp={(e) => void commit(Number((e.target as HTMLInputElement).value))}
        onTouchEnd={(e) => void commit(Number((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => void commit(Number((e.target as HTMLInputElement).value))}
      />
      <span className="aggr-val">{AGGR_LABEL[v]}</span>
    </label>
  );
}

function LeaveButton({ code, where }: { code: string; where: 'lobby' | 'game' }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    const verb = where === 'lobby' ? 'Deze lobby verlaten?' : 'Het spel verlaten? Je valt dan af.';
    if (!confirm(verb)) return;
    setBusy(true);
    try {
      await api.leave(code);
      navigate({ name: 'splash' });
    } catch (err) {
      console.error('leave failed', err);
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'verlaten mislukt');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="cta leave-btn" onClick={() => void go()} disabled={busy}>
      {busy ? '…' : where === 'lobby' ? 'lobby verlaten' : 'spel verlaten'}
    </button>
  );
}

export { LeaveButton };
